/**
 * useInbox Hook
 *
 * React Query hook for fetching and managing inbox messages via
 * NotificationService. Replaces the previous stub with real API calls.
 *
 * Features:
 * - Fetches messages from GET /notifications via useQuery
 * - Computes unreadCount client-side (count of messages where read === false)
 * - markAsRead via useMutation with optimistic update
 * - markAllAsRead via useMutation with optimistic update
 *
 * Requirements: 7.1, 7.2, 7.3, 7.4
 *
 * @module hooks/useInbox
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { NotificationService } from '../services/notification-service';
import { queryKeys } from '../config/query-client';
import type { Notification } from '../types';

/** Shape returned by the useInbox hook. */
export interface UseInboxReturn {
    messages: Notification[];
    unreadCount: number;
    isLoading: boolean;
    error: Error | null;
    markAsRead: (messageId: string) => void;
    markAllAsRead: () => void;
    isMarkingAsRead: boolean;
    isMarkingAllAsRead: boolean;
}

/**
 * Hook for fetching inbox messages and managing read state.
 *
 * Uses React Query for caching, automatic refetch on window focus,
 * and optimistic updates for mark-as-read operations.
 */
export function useInbox(options?: { enabled?: boolean }): UseInboxReturn {
    const { isAuthenticated } = useAuth();
    const queryClient = useQueryClient();

    // --- Query: fetch messages ---
    const {
        data: messages = [],
        isLoading,
        error,
    } = useQuery<Notification[], Error>({
        queryKey: queryKeys.inbox,
        queryFn: async (): Promise<Notification[]> => {
            if (!isAuthenticated) return [];
            const service = new NotificationService();
            return service.listNotifications();
        },
        enabled: options?.enabled !== false,
        refetchInterval: 30_000,
        refetchOnWindowFocus: true,
    });

    // --- Computed: unread count (Req 7.2) ---
    const unreadCount = useMemo(
        () => messages.filter((m) => m.read === false).length,
        [messages],
    );

    // --- Refetch inbox when profile is created/refreshed (catches welcome messages) ---
    useEffect(() => {
        const handler = () => {
            // Short delay to let the profile Lambda finish writing notifications
            setTimeout(() => {
                queryClient.invalidateQueries({ queryKey: queryKeys.inbox });
            }, 2000);
        };
        window.addEventListener('profile-refresh', handler);
        return () => window.removeEventListener('profile-refresh', handler);
    }, [queryClient]);

    // --- Mutation: mark single message as read (Req 7.3) ---
    const markAsReadMutation = useMutation({
        mutationFn: async (notificationId: string): Promise<void> => {
            if (!isAuthenticated) return;
            const service = new NotificationService();
            await service.markAsRead(notificationId);
        },
        onMutate: async (notificationId: string) => {
            // Cancel outgoing refetches
            await queryClient.cancelQueries({ queryKey: queryKeys.inbox });

            // Snapshot for rollback
            const previous = queryClient.getQueryData<Notification[]>(queryKeys.inbox);

            // Optimistic update
            queryClient.setQueryData<Notification[]>(queryKeys.inbox, (old) => {
                if (!old) return old;
                return old.map((m) =>
                    m.notification_id === notificationId
                        ? { ...m, read: true, read_timestamp: Date.now() }
                        : m,
                );
            });

            return { previous };
        },
        onError: (_err, _notificationId, context) => {
            // Rollback on failure
            if (context?.previous) {
                queryClient.setQueryData(queryKeys.inbox, context.previous);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.inbox });
        },
    });

    // --- Mutation: mark all messages as read (Req 7.4) ---
    const markAllAsReadMutation = useMutation({
        mutationFn: async (): Promise<void> => {
            if (!isAuthenticated) return;
            const service = new NotificationService();
            await service.markAllAsRead();
        },
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: queryKeys.inbox });

            const previous = queryClient.getQueryData<Notification[]>(queryKeys.inbox);

            // Optimistic: mark every message as read
            queryClient.setQueryData<Notification[]>(queryKeys.inbox, (old) => {
                if (!old) return old;
                return old.map((m) =>
                    m.read
                        ? m
                        : { ...m, read: true, read_timestamp: Date.now() },
                );
            });

            return { previous };
        },
        onError: (_err, _vars, context) => {
            if (context?.previous) {
                queryClient.setQueryData(queryKeys.inbox, context.previous);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.inbox });
        },
    });

    return {
        messages,
        unreadCount,
        isLoading,
        error: error ?? null,
        markAsRead: markAsReadMutation.mutate,
        markAllAsRead: () => markAllAsReadMutation.mutate(),
        isMarkingAsRead: markAsReadMutation.isPending,
        isMarkingAllAsRead: markAllAsReadMutation.isPending,
    };
}
